import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  FileText,
  Navigation,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  FileDown,
  Filter,
  RefreshCw,
  AlertCircle,
  Truck,
  User,
  MapPin,
  Calendar,
  Clock,
  Package,
  Route,
  Play,
  Pause,
  Square,
  Phone,
  MessageSquare
} from 'lucide-react';

// Mock data cho chuyến đi
const mockTrips = [
  {
    id: 'TRIP001',
    routeCode: 'HN-DN-001',
    name: 'Chuyến Hà Nội - Đà Nẵng',
    route: 'Hà Nội → Vinh → Huế → Đà Nẵng',
    driverName: 'Nguyễn Văn A',
    driverPhone: '0901234567',
    vehicleNumber: '30A-12345',
    vehicleBrand: 'Hyundai Universe',
    status: 'in_progress',
    departure: '2024-07-14T08:00',
    estimatedArrival: '2024-07-14T20:00',
    actualArrival: null,
    fromHub: 'Bưu cục Hà Nội',
    toHub: 'Bưu cục Đà Nẵng',
    totalOrders: 45,
    totalBags: 8,
    totalWeight: 850.5,
    notes: 'Chuyến đi thường xuyên, ưu tiên hàng hóa',
    createdAt: '2024-07-14T07:30',
    acceptedAt: '2024-07-14T07:45',
    orders: ['DH001', 'DH002', 'DH003'],
    bags: ['BAG001', 'BAG002'],
  },
  {
    id: 'TRIP002',
    routeCode: 'HCM-CT-002',
    name: 'Chuyến TP.HCM - Cần Thơ',
    route: 'TP.HCM → Long An → Tiền Giang → Cần Thơ',
    driverName: 'Trần Văn B',
    driverPhone: '0987654321',
    vehicleNumber: '51B-67890',
    vehicleBrand: 'Mercedes Sprinter',
    status: 'completed',
    departure: '2024-07-13T14:00',
    estimatedArrival: '2024-07-13T18:00',
    actualArrival: '2024-07-13T17:45',
    fromHub: 'Bưu cục TP.HCM',
    toHub: 'Bưu cục Cần Thơ',
    totalOrders: 28,
    totalBags: 5,
    totalWeight: 420.8,
    notes: 'Giao hàng thành công, không có sự cố',
    createdAt: '2024-07-13T13:30',
    acceptedAt: '2024-07-13T13:40',
    orders: ['DH004', 'DH005'],
    bags: ['BAG003'],
  },
  {
    id: 'TRIP003',
    routeCode: 'DN-QN-003',
    name: 'Chuyến Đà Nẵng - Quảng Nam',
    route: 'Đà Nẵng → Hội An → Tam Kỳ',
    driverName: 'Lê Văn C',
    driverPhone: '0369852147',
    vehicleNumber: '43C-11111',
    vehicleBrand: 'Isuzu NPR',
    status: 'pending',
    departure: '2024-07-15T06:00',
    estimatedArrival: '2024-07-15T12:00',
    actualArrival: null,
    fromHub: 'Bưu cục Đà Nẵng',
    toHub: 'Bưu cục Quảng Nam',
    totalOrders: 15,
    totalBags: 3,
    totalWeight: 180.2,
    notes: 'Chuyến đi mới, cần xác nhận tài xế',
    createdAt: '2024-07-14T15:30',
    acceptedAt: null,
    orders: ['DH006'],
    bags: ['BAG004'],
  },
  {
    id: 'TRIP004',
    routeCode: 'HN-HP-004',
    name: 'Chuyến Hà Nội - Hải Phòng',
    route: 'Hà Nội → Hưng Yên → Hải Dương → Hải Phòng',
    driverName: 'Phạm Văn D',
    driverPhone: '0912345678',
    vehicleNumber: '29D-22222',
    vehicleBrand: 'Thaco Ollin',
    status: 'delayed',
    departure: '2024-07-14T10:00',
    estimatedArrival: '2024-07-14T14:00',
    actualArrival: null,
    fromHub: 'Bưu cục Hà Nội',
    toHub: 'Bưu cục Hải Phòng',
    totalOrders: 32,
    totalBags: 6,
    totalWeight: 640.3,
    notes: 'Xe bị hỏng trên đường, đang sửa chữa',
    createdAt: '2024-07-14T09:30',
    acceptedAt: '2024-07-14T09:45',
    orders: ['DH007', 'DH008'],
    bags: ['BAG005', 'BAG006'],
  }
];

const TripManagement = () => {
  const [trips, setTrips] = useState(mockTrips);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState<any>(null);
  const [viewingTrip, setViewingTrip] = useState<any>(null);
  const [deletingTrip, setDeletingTrip] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState({
    routeCode: '',
    name: '',
    route: '',
    driverName: '',
    driverPhone: '',
    vehicleNumber: '',
    vehicleBrand: '',
    departure: '',
    estimatedArrival: '',
    fromHub: '',
    toHub: '',
    notes: '',
    orders: [] as string[],
    bags: [] as string[]
  });

  const resetForm = () => {
    setFormData({
      routeCode: '',
      name: '',
      route: '',
      driverName: '',
      driverPhone: '',
      vehicleNumber: '',
      vehicleBrand: '',
      departure: '',
      estimatedArrival: '',
      fromHub: '',
      toHub: '',
      notes: '',
      orders: [],
      bags: []
    });
  };

  const handleCreateTrip = () => {
    resetForm();
    setEditingTrip(null);
    setShowCreateDialog(true);
  };

  const filteredTrips = trips.filter(trip => {
    const matchesSearch = trip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trip.routeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trip.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trip.vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || trip.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'delayed':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ xuất phát';
      case 'in_progress': return 'Đang di chuyển';
      case 'completed': return 'Hoàn thành';
      case 'cancelled': return 'Đã hủy';
      case 'delayed': return 'Bị trì hoãn';
      default: return 'Không xác định';
    }
  };

  const handleEditTrip = (trip: any) => {
    setEditingTrip(trip);
    setFormData({
      routeCode: trip.routeCode,
      name: trip.name,
      route: trip.route,
      driverName: trip.driverName,
      driverPhone: trip.driverPhone,
      vehicleNumber: trip.vehicleNumber,
      vehicleBrand: trip.vehicleBrand,
      departure: trip.departure,
      estimatedArrival: trip.estimatedArrival,
      fromHub: trip.fromHub,
      toHub: trip.toHub,
      notes: trip.notes,
      orders: trip.orders,
      bags: trip.bags
    });
    setShowCreateDialog(true);
  };

  const handleUpdateTrip = () => {
    if (editingTrip) {
      const updatedTrips = trips.map(trip => 
        trip.id === editingTrip.id 
          ? { ...trip, ...formData }
          : trip
      );
      setTrips(updatedTrips);
      toast({
        title: "Cập nhật thành công",
        description: "Thông tin chuyến đi đã được cập nhật",
      });
    } else {
      const newTrip = {
        id: `TRIP${String(trips.length + 1).padStart(3, '0')}`,
        ...formData,
        status: 'pending',
        actualArrival: null,
        totalOrders: formData.orders.length,
        totalBags: formData.bags.length,
        totalWeight: Math.random() * 1000,
        createdAt: new Date().toISOString(),
        acceptedAt: null
      };
      setTrips([...trips, newTrip]);
      toast({
        title: "Tạo mới thành công",
        description: "Chuyến đi mới đã được tạo",
      });
    }
    setShowCreateDialog(false);
    resetForm();
  };

  const handleDeleteTrip = (trip: any) => {
    setDeletingTrip(trip);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTrip = () => {
    if (deletingTrip) {
      const updatedTrips = trips.filter(trip => trip.id !== deletingTrip.id);
      setTrips(updatedTrips);
      toast({
        title: "Xóa thành công",
        description: "Chuyến đi đã được xóa khỏi hệ thống",
      });
    }
    setShowDeleteDialog(false);
    setDeletingTrip(null);
  };

  const handleViewTrip = (trip: any) => {
    setViewingTrip(trip);
    setShowViewDialog(true);
  };

  const handleSelectTrip = (tripId: string) => {
    setSelectedTrips(prev => 
      prev.includes(tripId) 
        ? prev.filter(id => id !== tripId)
        : [...prev, tripId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTrips.length === filteredTrips.length) {
      setSelectedTrips([]);
    } else {
      setSelectedTrips(filteredTrips.map(trip => trip.id));
    }
  };

  const handleBatchAction = (action: string) => {
    switch (action) {
      case 'start':
        // Logic to start selected trips
        toast({
          title: "Bắt đầu chuyến đi",
          description: `Đã bắt đầu ${selectedTrips.length} chuyến đi`,
        });
        break;
      case 'complete':
        // Logic to complete selected trips
        toast({
          title: "Hoàn thành chuyến đi",
          description: `Đã hoàn thành ${selectedTrips.length} chuyến đi`,
        });
        break;
      case 'cancel':
        // Logic to cancel selected trips
        toast({
          title: "Hủy chuyến đi",
          description: `Đã hủy ${selectedTrips.length} chuyến đi`,
        });
        break;
    }
    setSelectedTrips([]);
  };

  const handleExport = () => {
    toast({
      title: "Xuất báo cáo",
      description: "Đang xuất báo cáo chuyến đi...",
    });
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('vi-VN');
  };

  const formatDate = (dateTime: string) => {
    return new Date(dateTime).toLocaleDateString('vi-VN');
  };

  const formatTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleTimeString('vi-VN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateDuration = (departure: string, arrival: string) => {
    const start = new Date(departure);
    const end = new Date(arrival);
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h${minutes}m`;
  };

  const handleCallDriver = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Quản lý chuyến đi</h1>
          <p className="text-muted-foreground">Theo dõi và quản lý các chuyến đi vận chuyển</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <FileDown className="h-4 w-4 mr-2" />
            Xuất báo cáo
          </Button>
          <Button onClick={handleCreateTrip}>
            <Plus className="h-4 w-4 mr-2" />
            Tạo chuyến đi
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng chuyến đi</p>
                <p className="text-2xl font-bold">{trips.length}</p>
              </div>
              <Navigation className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Đang di chuyển</p>
                <p className="text-2xl font-bold text-blue-600">
                  {trips.filter(t => t.status === 'in_progress').length}
                </p>
              </div>
              <Truck className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Hoàn thành</p>
                <p className="text-2xl font-bold text-green-600">
                  {trips.filter(t => t.status === 'completed').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cần xử lý</p>
                <p className="text-2xl font-bold text-red-600">
                  {trips.filter(t => ['pending', 'delayed'].includes(t.status)).length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm theo tên, mã, tài xế, biển số xe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Lọc theo trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả trạng thái</SelectItem>
                <SelectItem value="pending">Chờ xuất phát</SelectItem>
                <SelectItem value="in_progress">Đang di chuyển</SelectItem>
                <SelectItem value="completed">Hoàn thành</SelectItem>
                <SelectItem value="cancelled">Đã hủy</SelectItem>
                <SelectItem value="delayed">Bị trì hoãn</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Làm mới
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch Actions */}
      {selectedTrips.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Đã chọn {selectedTrips.length} chuyến đi
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleBatchAction('start')}>
                  <Play className="h-4 w-4 mr-2" />
                  Bắt đầu
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBatchAction('complete')}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Hoàn thành
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBatchAction('cancel')}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Hủy
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trips Table */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách chuyến đi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedTrips.length === filteredTrips.length && filteredTrips.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Mã/Tên chuyến đi</TableHead>
                  <TableHead>Tuyến đường</TableHead>
                  <TableHead>Tài xế/Xe</TableHead>
                  <TableHead>Thời gian</TableHead>
                  <TableHead>Hàng hóa</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead>Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrips.map((trip) => (
                  <TableRow key={trip.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedTrips.includes(trip.id)}
                        onCheckedChange={() => handleSelectTrip(trip.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{trip.routeCode}</div>
                        <div className="text-sm text-muted-foreground">{trip.name}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-1">
                        <Route className="h-3 w-3 text-muted-foreground mt-1 flex-shrink-0" />
                        <span className="text-sm">{trip.route}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-sm">{trip.driverName}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleCallDriver(trip.driverPhone)}
                          >
                            <Phone className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Truck className="h-3 w-3" />
                          <span className="text-sm">{trip.vehicleNumber}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(trip.departure)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>{formatTime(trip.departure)} - {formatTime(trip.estimatedArrival)}</span>
                        </div>
                        {trip.actualArrival && (
                          <div className="text-xs text-green-600">
                            Hoàn thành: {formatTime(trip.actualArrival)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          <span>{trip.totalOrders} đơn</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <span>{trip.totalBags} bao</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {trip.totalWeight.toFixed(1)} kg
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(trip.status)}>
                        {getStatusText(trip.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewTrip(trip)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEditTrip(trip)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTrip(trip)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Trip Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTrip ? 'Chỉnh sửa chuyến đi' : 'Tạo chuyến đi mới'}
            </DialogTitle>
            <DialogDescription>
              {editingTrip ? 'Cập nhật thông tin chuyến đi' : 'Nhập thông tin để tạo chuyến đi mới'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Thông tin cơ bản</TabsTrigger>
              <TabsTrigger value="schedule">Lịch trình</TabsTrigger>
              <TabsTrigger value="cargo">Hàng hóa</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="routeCode">Mã tuyến đường *</Label>
                  <Input
                    id="routeCode"
                    value={formData.routeCode}
                    onChange={(e) => setFormData({...formData, routeCode: e.target.value})}
                    placeholder="HN-DN-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Tên chuyến đi *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Chuyến Hà Nội - Đà Nẵng"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="route">Tuyến đường *</Label>
                  <Input
                    id="route"
                    value={formData.route}
                    onChange={(e) => setFormData({...formData, route: e.target.value})}
                    placeholder="Hà Nội → Vinh → Huế → Đà Nẵng"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driverName">Tên tài xế *</Label>
                  <Input
                    id="driverName"
                    value={formData.driverName}
                    onChange={(e) => setFormData({...formData, driverName: e.target.value})}
                    placeholder="Nguyễn Văn A"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driverPhone">Số điện thoại tài xế *</Label>
                  <Input
                    id="driverPhone"
                    value={formData.driverPhone}
                    onChange={(e) => setFormData({...formData, driverPhone: e.target.value})}
                    placeholder="0901234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleNumber">Biển số xe *</Label>
                  <Input
                    id="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={(e) => setFormData({...formData, vehicleNumber: e.target.value})}
                    placeholder="30A-12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleBrand">Hãng xe</Label>
                  <Input
                    id="vehicleBrand"
                    value={formData.vehicleBrand}
                    onChange={(e) => setFormData({...formData, vehicleBrand: e.target.value})}
                    placeholder="Hyundai Universe"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fromHub">Bưu cục xuất phát *</Label>
                  <Select 
                    value={formData.fromHub} 
                    onValueChange={(value) => setFormData({...formData, fromHub: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn bưu cục xuất phát" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bưu cục Hà Nội">Bưu cục Hà Nội</SelectItem>
                      <SelectItem value="Bưu cục TP.HCM">Bưu cục TP.HCM</SelectItem>
                      <SelectItem value="Bưu cục Đà Nẵng">Bưu cục Đà Nẵng</SelectItem>
                      <SelectItem value="Bưu cục Hải Phòng">Bưu cục Hải Phòng</SelectItem>
                      <SelectItem value="Bưu cục Cần Thơ">Bưu cục Cần Thơ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="toHub">Bưu cục đích *</Label>
                  <Select 
                    value={formData.toHub} 
                    onValueChange={(value) => setFormData({...formData, toHub: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn bưu cục đích" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bưu cục Hà Nội">Bưu cục Hà Nội</SelectItem>
                      <SelectItem value="Bưu cục TP.HCM">Bưu cục TP.HCM</SelectItem>
                      <SelectItem value="Bưu cục Đà Nẵng">Bưu cục Đà Nẵng</SelectItem>
                      <SelectItem value="Bưu cục Hải Phòng">Bưu cục Hải Phòng</SelectItem>
                      <SelectItem value="Bưu cục Cần Thơ">Bưu cục Cần Thơ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="departure">Thời gian xuất phát *</Label>
                  <Input
                    id="departure"
                    type="datetime-local"
                    value={formData.departure}
                    onChange={(e) => setFormData({...formData, departure: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimatedArrival">Thời gian dự kiến đến *</Label>
                  <Input
                    id="estimatedArrival"
                    type="datetime-local"
                    value={formData.estimatedArrival}
                    onChange={(e) => setFormData({...formData, estimatedArrival: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Ghi chú</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Nhập ghi chú cho chuyến đi..."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="cargo" className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                Chọn đơn hàng và bao trung chuyển cho chuyến đi này
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Đơn hàng</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {['DH001', 'DH002', 'DH003', 'DH004', 'DH005', 'DH006', 'DH007', 'DH008'].map((order) => (
                      <div key={order} className="flex items-center space-x-2">
                        <Checkbox
                          id={order}
                          checked={formData.orders.includes(order)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({...formData, orders: [...formData.orders, order]});
                            } else {
                              setFormData({...formData, orders: formData.orders.filter(o => o !== order)});
                            }
                          }}
                        />
                        <Label htmlFor={order} className="text-sm cursor-pointer">
                          {order} - Nguyễn Văn A → Trần Văn B
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Bao trung chuyển</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {['BAG001', 'BAG002', 'BAG003', 'BAG004', 'BAG005', 'BAG006'].map((bag) => (
                      <div key={bag} className="flex items-center space-x-2">
                        <Checkbox
                          id={bag}
                          checked={formData.bags.includes(bag)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({...formData, bags: [...formData.bags, bag]});
                            } else {
                              setFormData({...formData, bags: formData.bags.filter(b => b !== bag)});
                            }
                          }}
                        />
                        <Label htmlFor={bag} className="text-sm cursor-pointer">
                          {bag} - 15 đơn hàng (120kg)
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Hủy
            </Button>
            <Button onClick={handleUpdateTrip}>
              {editingTrip ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Trip Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Chi tiết chuyến đi {viewingTrip?.routeCode}</DialogTitle>
          </DialogHeader>
          
          {viewingTrip && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Thông tin chuyến đi</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Mã tuyến:</strong> {viewingTrip.routeCode}</div>
                    <div><strong>Tên:</strong> {viewingTrip.name}</div>
                    <div><strong>Tuyến đường:</strong> {viewingTrip.route}</div>
                    <div><strong>Trạng thái:</strong> 
                      <Badge className={`ml-2 ${getStatusColor(viewingTrip.status)}`}>
                        {getStatusText(viewingTrip.status)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Tài xế & Phương tiện</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Tài xế:</strong> {viewingTrip.driverName}</div>
                    <div className="flex items-center gap-2">
                      <strong>Điện thoại:</strong> 
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCallDriver(viewingTrip.driverPhone)}
                        className="h-6"
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        {viewingTrip.driverPhone}
                      </Button>
                    </div>
                    <div><strong>Biển số:</strong> {viewingTrip.vehicleNumber}</div>
                    <div><strong>Hãng xe:</strong> {viewingTrip.vehicleBrand}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Lịch trình</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Xuất phát:</strong> {viewingTrip.fromHub}</div>
                    <div><strong>Đích đến:</strong> {viewingTrip.toHub}</div>
                    <div><strong>Thời gian xuất phát:</strong> {formatDateTime(viewingTrip.departure)}</div>
                    <div><strong>Dự kiến đến:</strong> {formatDateTime(viewingTrip.estimatedArrival)}</div>
                    {viewingTrip.actualArrival && (
                      <div><strong>Thực tế đến:</strong> {formatDateTime(viewingTrip.actualArrival)}</div>
                    )}
                    <div><strong>Thời gian di chuyển:</strong> 
                      {calculateDuration(viewingTrip.departure, viewingTrip.actualArrival || viewingTrip.estimatedArrival)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Hàng hóa</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Tổng đơn hàng:</strong> {viewingTrip.totalOrders}</div>
                    <div><strong>Tổng bao:</strong> {viewingTrip.totalBags}</div>
                    <div><strong>Tổng trọng lượng:</strong> {viewingTrip.totalWeight.toFixed(1)} kg</div>
                  </CardContent>
                </Card>
              </div>

              {viewingTrip.notes && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Ghi chú</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{viewingTrip.notes}</p>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Đơn hàng ({viewingTrip.orders.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 text-sm">
                      {viewingTrip.orders.map((order: string) => (
                        <div key={order} className="flex items-center gap-2">
                          <Package className="h-3 w-3" />
                          <span>{order}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Bao trung chuyển ({viewingTrip.bags.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 text-sm">
                      {viewingTrip.bags.map((bag: string) => (
                        <div key={bag} className="flex items-center gap-2">
                          <Package className="h-3 w-3" />
                          <span>{bag}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Trip Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa chuyến đi</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa chuyến đi "{deletingTrip?.name}"? 
              Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTrip}>
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TripManagement; 